// Catalog.ID Client — Module Registration Consent Modal
// ============================================================================
//
// Modal shown the first time a dashboard module publishes its identity to this
// account. Visual language follows the existing OIDC consent pane — CardGroup
// of module info, then a Register/Deny choice.
//
// This file also held `showKeyGrantConsent`, the Allow-once/session/always
// dialog for the generic `release` and `save` modes, until cid-encrypted-storage
// M6b (2026-09-17) retired both modes: no key-grant envelope asks for raw bag
// material any more, so nothing had that question to put to the member. The
// consent dialogs that DO remain live beside the families that raise them
// (`bitcash-consent-modal`, `bitcash-wallets-consent-modal`,
// `chat-address-consent-modal`, `mail-*`, `registry-key-consent-modal`,
// `ceremony-consent-modal`), each with its own vocabulary of decisions.
// ============================================================================

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, escapeHtml, createElement } from '@catalog-shell';
import type { KeyGrantRequest } from './types';
import { moduleName } from './module-names';

export type RegistrationDecision = 'register' | 'deny';

/** Everything the dialog says, decided without a DOM (desktop-setup §6.3). */
export interface RegistrationConsentCopy {
    /** The module in the member's words — "Files", not "efs" and not
     *  "Catalog Encrypted File Storage". */
    name: string;
    /** The service id, shown second and never instead. */
    serviceId: string;
    /** The first 16 characters of the key this module claims, or null when it
     *  sent none (the signature gate refuses that request anyway). */
    fingerprint: string | null;
    /** The sentence the decision hangs on. Bold on screen. */
    lead: string;
    /** What approving means, and how to undo it. */
    body: string;
}

/**
 * The consent's words (desktop-setup §6.3).
 *
 * The module sends `requesterLabel` about itself — today "Catalog Public
 * Registry" and "Catalog Encrypted File Storage" — and the warning used to
 * name it only by its service id ("keys under `cpr`"). Neither is what the
 * member has been reading: the Registry and Files call themselves Registry and
 * Files in their own guides and refusals, including the one that sent the
 * member here. The plain name leads, the id follows it, and the way out names
 * the same pane in the same words those modules use.
 */
export function describeRegistrationConsent(
    request: KeyGrantRequest,
): RegistrationConsentCopy {
    const name = moduleName(request.requesterId, request.requesterLabel);
    return {
        name,
        serviceId: request.requesterId,
        fingerprint: request.signerPubkey ? request.signerPubkey.slice(0, 16) + '\u2026' : null,
        lead: `Approve only if you just installed ${name} yourself.`,
        body: `Once you approve, only this copy of ${name} can ask your Catalog.ID for `
            + `keys — anything else claiming to be ${name} (service id ${request.requesterId}) `
            + 'is refused. If you change your mind, open Connected Modules and forget '
            + `${possessive(name)} registered identity.`,
    };
}

/** "Files" → "Files'", "Registry" → "Registry's" — the form Files and the
 *  Registry already use in the copy that sends the member here. */
function possessive(name: string): string {
    return name.endsWith('s') ? `${name}'` : `${name}'s`;
}

/**
 * Modal shown the FIRST time a dashboard module attempts to register its
 * Ed25519 identity with this catalog.id account. Subsequent registration
 * attempts under the same moduleId hit the first-writer-wins gate before
 * reaching this modal.
 *
 * Audit fix C2: making the first registration explicit blocks the
 * race-and-anchor attack where local malware registers itself as "xrpl"
 * before the legitimate xrpl module starts up.
 */
export function showModuleRegistrationConsent(
    shell: Shell,
    request: KeyGrantRequest,
): Promise<RegistrationDecision> {
    return new Promise<RegistrationDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: RegistrationDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const copy = describeRegistrationConsent(request);
        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        // The title is quoted verbatim by other modules' copy — Files tells the
        // member to approve 'Register a new dashboard module?' in the
        // Catalog.ID tab — and by their tests, so it stays as it is. The
        // module's NAME lands in the rows and the warning below instead.
        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: 'Register a new dashboard module?',
        }));

        const moduleGroup = createFieldGroup({ title: 'Module' });

        // Plain name first (§6.3) — "Files", not the long label the module
        // sends about itself, and never the service id alone.
        const labelRow = createFormRow({ label: 'Name' });
        labelRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: copy.name,
        }));
        moduleGroup.body.appendChild(labelRow.element);

        const idRow = createFormRow({ label: 'Service ID' });
        idRow.content.appendChild(createElement('code', { textContent: copy.serviceId }));
        moduleGroup.body.appendChild(idRow.element);

        // Show a fingerprint of the pubkey so a vigilant user can spot a
        // re-registration attempt with a different key (in practice the first
        // registration is the one that anchors — re-registrations are rejected
        // by the first-writer-wins gate without showing this modal).
        if (copy.fingerprint) {
            const fpRow = createFormRow({ label: 'Identity fingerprint' });
            fpRow.content.appendChild(createElement('code', {
                textContent: copy.fingerprint,
            }));
            moduleGroup.body.appendChild(fpRow.element);
        }
        container.appendChild(moduleGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            innerHTML: `<strong>${escapeHtml(copy.lead)}</strong> ${escapeHtml(copy.body)}`,
        }));

        const actions = createElement('div', { className: 'cs-form-actions' });

        const btnDeny = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Deny',
        }) as HTMLButtonElement;
        btnDeny.addEventListener('click', () => resolveOnce('deny'));
        actions.appendChild(btnDeny);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Register module',
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('register'));
        actions.appendChild(btnApprove);

        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'normal',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            onClose: () => resolveOnce('deny'),
        });
    });
}
